
import { ViewModeMixin } from '@/utils/viewMode.mixin';
import { mixins } from 'vue-class-component';
import { Component, Prop, Provide, Watch } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { FullDocumentRevisionObject } from '@/jbi-shared/types/full-document-revision-object.types';
import StagingCriterionFormStepOne from './StagingCriterionFormStepOne.vue';
import StagingCriterionFormStepTwo from './StagingCriterionFormStepTwo.vue';
import AuditCriteriaRemovalWarning from './AuditCriteriaRemovalWarning.vue';
import { PendingCriterionData } from '@/jbi-shared/types/criterions.types';
import { DirtyTagMap } from '@/store/modules/documents/types/documents.types';
import { deepEqual } from '@/jbi-shared/util/deep-copy.util';

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    StagingCriterionFormStepOne,
    StagingCriterionFormStepTwo,
    AuditCriteriaRemovalWarning
  }
})
export default class StagingCriterionForm extends mixins(ViewModeMixin) {
  @Prop()
  public existingCriterions!: FullDocumentRevisionObject['revision']['sections']['criterionSection'];
  @Prop()
  public existingBprs!: FullDocumentRevisionObject['revision']['sections']['bprSection'];
  @Prop()
  public validCriterions!: PendingCriterionData[];
  @Prop()
  public invalidCriterions!: PendingCriterionData[];
  @Prop()
  public dirtyCriterions!: PendingCriterionData[];
  @Prop()
  public dirtyBprs!: FullDocumentRevisionObject['revision']['sections']['bprSection'];
  @Prop()
  public newlyAddedCriterions!: PendingCriterionData[];
  @Prop()
  public selectedCriterions!: PendingCriterionData[];
  @Prop()
  public dirtyTagMaps!: DirtyTagMap[];
  public criterionFormDirtyTagMaps =
    JSON.parse(JSON.stringify(this.dirtyTagMaps)) || [];

  public internalSelectedCriterions: PendingCriterionData[] = [];
  public activeStep = 1;
  public updateCounter = 0; // Add counter to force reactivity

  // Internal reactive data that gets updated when new criteria are added
  public internalDirtyCriterions: PendingCriterionData[] = [];
  public internalNewlyAddedCriterions: PendingCriterionData[] = [];
  public internalValidCriterions: PendingCriterionData[] = [];
  public internalInvalidCriterions: PendingCriterionData[] = [];
  public isInitialLoad = true;

  get hasInvalidSelectedCriterions() {
    const invalidCriterions = this.currentSelectedCriterions.filter(
      (selectedCriterion) => {
        const latestCriterion = this.latestDirtyCriterions.find((latest) =>
          this.criteriaVersionMatch(latest, selectedCriterion)
        );
        if (latestCriterion) {
          return !latestCriterion.isValid;
        }
        return !selectedCriterion.isValid;
      }
    );

    return invalidCriterions.length > 0;
  }

  // Use internal reactive data that updates in real-time
  get latestDirtyCriterions() {
    // Use internal data if it has been initialized, otherwise use props
    return this.internalDirtyCriterions.length > 0 || this.updateCounter > 0
      ? this.internalDirtyCriterions
      : this.dirtyCriterions;
  }

  get latestNewlyAddedCriterions() {
    // Use internal data if it has been initialized, otherwise use props
    return this.internalNewlyAddedCriterions.length > 0 ||
      this.updateCounter > 0
      ? this.internalNewlyAddedCriterions
      : this.newlyAddedCriterions;
  }

  get latestValidCriterions() {
    // Use internal data if it has been initialized, otherwise use props
    return this.internalValidCriterions.length > 0 || this.updateCounter > 0
      ? this.internalValidCriterions
      : this.validCriterions;
  }

  get latestInvalidCriterions() {
    // Use internal data if it has been initialized, otherwise use props
    return this.internalInvalidCriterions.length > 0 || this.updateCounter > 0
      ? this.internalInvalidCriterions
      : this.invalidCriterions;
  }

  // Computed property to manage selected criteria state
  get currentSelectedCriterions() {
    // Use internal selected criteria if we have them, otherwise use prop
    const result = this.internalSelectedCriterions;
    // important guys important use result to return the internal selected criterions and to debug in case it
    return result;
  }

  set currentSelectedCriterions(value: PendingCriterionData[]) {
    this.internalSelectedCriterions = value;
  }

  @Watch('currentSelectedCriterions')
  onSelectedCriterionsChange(newValue: any, oldValue: any) {
    this.isInitialLoad = false;
  }

  public nextStep() {
    if (this.activeStep < 2) {
      // Before moving to step 2/criterion settings, refresh selected criteria with their latest versions
      this.refreshSelectedCriteriaWithLatestVersions();
      this.activeStep += 1;
    }
  }

  public prevStep() {
    if (this.activeStep > 1) {
      this.activeStep -= 1;
    }
  }

  public saveCriterions() {
    if (this.willRemoveExistingCriteria()) {
      this.showWarningModal('save');
      return;
    }

    this.performSave();
  }

  private performSave() {
    const finalSelection = this.currentSelectedCriterions;

    // Emit the final selections to update the parent component's state
    this.$emit('update:criterions', finalSelection);
    this.$emit('update:selectedCriterions', finalSelection);
    this.$emit('update:dirtyTagMaps', this.criterionFormDirtyTagMaps);
    this.$emit('close');
  }

  public criteriaMatch(
    criterion1: PendingCriterionData,
    criterion2: PendingCriterionData
  ): boolean {
    // Check if criteria match by comparing their unique identifiers
    // First try to match by tempId (for newly added criteria)
    if (criterion1.tempId && criterion2.tempId) {
      return criterion1.tempId === criterion2.tempId;
    }

    // If no tempId match, use the same comparison logic as used elsewhere in the codebase
    return (
      (criterion1.criterionSubSectionId &&
        criterion2.criterionSubSectionId &&
        criterion1.criterionSubSectionId > 0 &&
        criterion1.criterionSubSectionId ===
          criterion2.criterionSubSectionId) ||
      deepEqual(criterion1, criterion2)
    );
  }

  public criteriaVersionMatch(
    criterion1: PendingCriterionData,
    criterion2: PendingCriterionData
  ): boolean {
    if (criterion1.tempId && criterion2.tempId) {
      return criterion1.tempId === criterion2.tempId;
    }

    return deepEqual(criterion1, criterion2);
  }

  public refreshSelectedCriteriaWithLatestVersions() {
    // Update each selected criterion with its latest version from internal reactive data
    this.currentSelectedCriterions = this.currentSelectedCriterions.map(
      (selectedCriterion) => {
        const latestVersion = this.latestDirtyCriterions.find((latest) =>
          // Do NOT use criteriaMatch here:
          // criteriaMatch can match by criterionSubSectionId alone
          // even when content/title changed (different version).
          // That may replace a selected original criterion with its edited variant.
          // criteriaVersionMatch is stricter (tempId or deep-equal content),
          // so it preserves the exact selected version.
          this.criteriaVersionMatch(latest, selectedCriterion)
        );

        if (latestVersion) {
          return latestVersion;
        }

        return selectedCriterion;
      }
    );
  }

  // Warning modal methods
  public showWarningModal(pendingAction: 'save' | 'close') {
    const modalRef = this.$buefy.modal.open({
      parent: this,
      component: AuditCriteriaRemovalWarning,
      hasModalCard: true,
      canCancel: false,
      trapFocus: true,
      width: 600,
      events: {
        continue: () => {
          modalRef.close();
          if (pendingAction === 'save') {
            this.performSave();
          } else if (pendingAction === 'close') {
            this.performClose();
          }
        },
        cancel: () => {
          modalRef.close();
        }
      }
    });
  }

  public handleClose() {
    if (this.willRemoveExistingCriteria()) {
      this.showWarningModal('close');
      return;
    }

    this.performClose();
  }

  private performClose() {
    this.$emit('close');
  }

  private willRemoveExistingCriteria(): boolean {
    // Check if there are existing criteria
    if (!this.existingCriterions?.length) {
      return false;
    }

    // Check if any existing criteria are not selected
    return this.existingCriterions.some((existingCriterion: any) => {
      return !this.currentSelectedCriterions.find((selectedCriterion) =>
        this.criteriaMatch(selectedCriterion, existingCriterion)
      );
    });
  }

  public updateDirtyCriterions(updatedCriterions: PendingCriterionData[]) {
    // Update the dirtyCriterions and emit to parent
    this.$emit('update:criterions', updatedCriterions);
  }

  public onCriterionAdded(criterion: PendingCriterionData) {
    const updatedCriterions = [...this.dirtyCriterions, criterion];
    this.$emit('update:criterions', updatedCriterions);

    this.internalDirtyCriterions = [...this.internalDirtyCriterions, criterion];
    this.internalNewlyAddedCriterions = [
      ...this.internalNewlyAddedCriterions,
      criterion
    ];

    if (
      !this.currentSelectedCriterions.find((selected) =>
        this.criteriaMatch(selected, criterion)
      )
    ) {
      this.currentSelectedCriterions = [
        ...this.currentSelectedCriterions,
        criterion
      ];
    }

    // Force the computed properties to recalculate
    this.updateCounter++;

    // Force update this component and all children immediately
    this.$forceUpdate();

    // Also force update child components in next tick
    this.$nextTick(() => {
      this.$children.forEach((child) => {
        if (child.$forceUpdate) {
          child.$forceUpdate();
        }
      });
    });
  }

  public onCriterionUpdated(updatedCriterion: PendingCriterionData) {
    // Update the internal dirty criteria array with the edited criterion
    const existingIndex = this.internalDirtyCriterions.findIndex((criterion) =>
      this.criteriaMatch(criterion, updatedCriterion)
    );

    if (existingIndex !== -1) {
      // Update existing criterion in internal dirty criteria
      this.$set(this.internalDirtyCriterions, existingIndex, updatedCriterion);
    } else {
      // Add new criterion if not found (shouldn't happen normally)
      this.internalDirtyCriterions = [
        ...this.internalDirtyCriterions,
        updatedCriterion
      ];
    }

    // Also update the parent's dirty criteria
    const parentUpdatedCriterions = this.dirtyCriterions;
    const parentExistingIndex = parentUpdatedCriterions.findIndex((criterion) =>
      this.criteriaMatch(criterion, updatedCriterion)
    );

    if (parentExistingIndex !== -1) {
      parentUpdatedCriterions[parentExistingIndex] = updatedCriterion;
    } else {
      parentUpdatedCriterions.push(updatedCriterion);
    }

    this.$emit('update:criterions', parentUpdatedCriterions);
  }

  public onModalClosed() {
    // When a child modal closes, refresh the main modal data
    this.refreshData();
  }

  private async updateModalInternalData(newCriterion: PendingCriterionData) {
    // Find the parent pending editor to get the latest state
    let stagingEditor = this.$parent as any;
    while (stagingEditor && !stagingEditor.dirtyCriterions) {
      stagingEditor = stagingEditor.$parent as any;
    }

    if (stagingEditor && stagingEditor.dirtyCriterions) {
      try {
        // Get the latest frontend state (no database reload needed since criteria are frontend-only until main save)
        const freshDirtyCriterions = stagingEditor.dirtyCriterions || [];
        const freshNewlyAddedCriterions =
          stagingEditor.newlyAddedCriterions || [];
        const freshValidCriterions = stagingEditor.validCriterions || [];
        const freshInvalidCriterions = stagingEditor.invalidCriterions || [];

        // Update the modal's internal reactive data
        this.internalDirtyCriterions = [...freshDirtyCriterions];
        this.internalNewlyAddedCriterions = [...freshNewlyAddedCriterions];
        this.internalValidCriterions = [...freshValidCriterions];
        this.internalInvalidCriterions = [...freshInvalidCriterions];

        // Force the computed properties to recalculate
        this.updateCounter++;

        // Force update this component and all children
        this.$forceUpdate();

        await this.$nextTick();

        // Also force update child components
        this.$children.forEach((child) => {
          if (child.$forceUpdate) {
            child.$forceUpdate();
          }
        });
      } catch (error) {
        // Log errors
      }
    }
  }

  @Watch('dirtyCriterions', { deep: true, immediate: true })
  public onDirtyCriterionsChanged(
    newCriterions: PendingCriterionData[],
    oldCriterions: PendingCriterionData[]
  ) {
    // If criteria were added, update the internal state immediately
    if (newCriterions.length > (oldCriterions?.length || 0)) {
      // Increment counter to force computed properties to update
      this.updateCounter++;

      // Force child components to re-render with new data
      this.$nextTick(() => {
        this.$forceUpdate();

        // Also force update child components
        this.$children.forEach((child) => {
          if (child.$forceUpdate) {
            child.$forceUpdate();
          }
        });
      });
    }
  }

  public autoSelectIdenticalPreviousCriterion() {
    const remainder = this.dirtyCriterions.filter((criterion) => {
      return !this.newlyAddedCriterions.includes(criterion);
    });
    this.currentSelectedCriterions = this.currentSelectedCriterions.concat(
      remainder
    );
  }

  public mounted() {
    // Initialize internal reactive data with prop values
    this.internalDirtyCriterions = [...this.dirtyCriterions];
    this.internalNewlyAddedCriterions = [...this.newlyAddedCriterions];
    this.internalValidCriterions = [...this.validCriterions];
    this.internalInvalidCriterions = [...this.invalidCriterions];

    if (
      this.isInitialLoad &&
      (!this.selectedCriterions || this.selectedCriterions.length === 0)
    ) {
      this.autoSelectIdenticalPreviousCriterion();
    } else {
      this.internalSelectedCriterions = [...this.selectedCriterions];
    }

    // Increment counter to ensure computed properties use internal data
    this.updateCounter++;
  }

  public refreshData() {
    this.updateCounter++;
    this.$forceUpdate();

    // Also refresh child components
    this.$nextTick(() => {
      this.$children.forEach((child) => {
        if (child.$forceUpdate) {
          child.$forceUpdate();
        }
      });
    });
  }

  @Watch('newlyAddedCriterions', { deep: true, immediate: true })
  public onNewlyAddedCriterionsUpdated(newCriterions: PendingCriterionData[]) {
    // Force update when newly added criterions change
    this.updateCounter++;
    this.$forceUpdate();
  }

  // Watch for external prop changes and update internal state
  @Watch('dirtyCriterions', { immediate: true })
  public onDirtyCriterionsPropsChanged(newCriterions: PendingCriterionData[]) {
    // Update internal counter to trigger computed property updates
    this.updateCounter++;

    // Force immediate update of computed properties
    this.$nextTick(() => {
      this.$forceUpdate();
    });
  }
}
